/**
 * TypeChip Component
 *
 * Shared type badge/chip for displaying column types consistently
 * across input mapping, output mapping, and column selector.
 */

import { Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { STREAMLAIN_TYPE_CHIP_LAYOUT } from '@blocksuite/affine-shared/styles';
import {
  getTypeColor,
  normalizeTypeLabel,
} from '@blocksuite/affine-shared/utils';
import {
  AttachmentIcon,
  CheckBoxCheckLinearIcon,
  CodeIcon,
  DatabaseTableViewIcon,
  DateTimeIcon,
  ImageIcon,
  LinkIcon,
  MultiSelectIcon,
  NumberIcon,
  ProgressIcon,
  SingleSelectIcon,
  TextIcon,
} from '@blocksuite/icons/rc';
import { normalizeType, ROW_LIST_FIELD_TYPE } from '@streamlain/field-types';
import { cssVarV2 } from '@streamlain/theme/v2';
import type { FC } from 'react';

// Re-export for backwards compatibility
export { getTypeColor, normalizeTypeLabel };

/** Same glyphs as the database property renderers; workflow types retain their exact labels. */
const TYPE_ICONS = {
  text: TextIcon,
  string: TextIcon,
  title: TextIcon,
  'rich-text': TextIcon,
  number: NumberIcon,
  boolean: CheckBoxCheckLinearIcon,
  checkbox: CheckBoxCheckLinearIcon,
  select: SingleSelectIcon,
  'multi-select': MultiSelectIcon,
  array: MultiSelectIcon,
  date: DateTimeIcon,
  progress: ProgressIcon,
  file: AttachmentIcon,
  attachment: AttachmentIcon,
  image: ImageIcon,
  relation: LinkIcon,
  json: CodeIcon,
  object: CodeIcon,
  [ROW_LIST_FIELD_TYPE]: DatabaseTableViewIcon,
} as const;

interface TypeChipProps {
  type: string;
  /** Use smaller size for compact displays */
  small?: boolean;
  /** Override inline styles (e.g. remove marginLeft in flex containers) */
  style?: React.CSSProperties;
  /** Click handler (e.g. cycle type in editor) */
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * TypeChip - displays a type badge with consistent styling
 *
 * Quiet semantic surface, readable type name; shared by mapping and its preview.
 */
export const TypeChip: FC<TypeChipProps> = ({
  type,
  small = false,
  style: overrideStyle,
  onClick,
}) => {
  const t = useI18n();
  const canonical = normalizeType(type);
  const displayType =
    type.toLowerCase() === 'string'
      ? 'String'
      : canonical === ROW_LIST_FIELD_TYPE
        ? t['com.streamlain.automation.field-type.row-list']()
        : normalizeTypeLabel(type);
  const Icon = TYPE_ICONS[canonical as keyof typeof TYPE_ICONS];

  return (
    <span
      data-streamlain-type-chip
      title={type}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.currentTarget.click();
              }
            }
          : undefined
      }
      style={{
        ...STREAMLAIN_TYPE_CHIP_LAYOUT,
        gap: 3,
        fontSize: small ? '10px' : '11px',
        minHeight: small ? '16px' : STREAMLAIN_TYPE_CHIP_LAYOUT.minHeight,
        padding: small ? '0px 3px' : STREAMLAIN_TYPE_CHIP_LAYOUT.padding,
        lineHeight: STREAMLAIN_TYPE_CHIP_LAYOUT.lineHeight,
        backgroundColor: cssVarV2('layer/background/secondary'),
        color: cssVarV2('text/secondary'),
        border: `0.5px solid ${cssVarV2('layer/insideBorder/border')}`,
        borderRadius: 4,
        textTransform: 'none',
        marginLeft: '4px',
        ...overrideStyle,
      }}
    >
      {Icon && (
        <Icon
          width={small ? 11 : 12}
          height={small ? 11 : 12}
          aria-hidden="true"
        />
      )}
      {displayType}
    </span>
  );
};

// =============================================================================
// CompatDot — shared compatibility indicator circle
// =============================================================================

const COMPAT_COLORS = {
  compatible: cssVarV2('status/success'),
  castable: cssVarV2('button/primary'),
  incompatible: cssVarV2('status/error'),
} as const;

const COMPAT_LABELS = {
  compatible: '✓',
  castable: '↔',
  incompatible: '−',
} as const;

const COMPAT_TOOLTIPS = {
  compatible: 'com.streamlain.streamlain.workflow.types-compatible',
  castable: 'com.streamlain.streamlain.workflow.type-auto-cast',
  incompatible:
    'com.streamlain.streamlain.workflow.types-may-not-be-compatible',
} as const;

interface CompatDotProps {
  compatibility: 'compatible' | 'castable' | 'incompatible';
  /** Override tooltip text (default: generic message) */
  tooltip?: string;
  /** Override inline styles */
  style?: React.CSSProperties;
}

/**
 * CompatDot — 14x14 colored circle with ✓/~/! symbol and tooltip.
 * Shared across sidebar rows, dropdown options, and dock mapper.
 */
export const CompatDot: FC<CompatDotProps> = ({
  compatibility,
  tooltip,
  style: overrideStyle,
}) => {
  const t = useI18n();
  return (
    <Tooltip content={tooltip ?? t.t(COMPAT_TOOLTIPS[compatibility])}>
      <span
        aria-label={tooltip ?? t.t(COMPAT_TOOLTIPS[compatibility])}
        style={{
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          color: COMPAT_COLORS[compatibility],
          fontSize: '14px',
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: 'help',
          ...overrideStyle,
        }}
      >
        {COMPAT_LABELS[compatibility]}
      </span>
    </Tooltip>
  );
};
